
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Edit } from 'lucide-react';
import React from 'react';
import { useToast } from '@/hooks/use-toast';
import type { SavingsGoal } from '@/lib/types';
import { formatCurrency, getCurrentCurrencySymbol } from '@/lib/utils';
import { useAppStore } from '@/lib/data';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const formSchema = z.object({
  amount: z.coerce.number().positive('Amount must be positive'),
  keepOpen: z.boolean().default(false),
});

import { useAuth } from '@/hooks/use-auth';
import { StreakService, ACTIVITY_TYPES } from '@/lib/streak-service';

export function UpdateGoalSheet({ goal }: { goal: SavingsGoal }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const { updateSavingsGoal } = useAppStore();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0,
      keepOpen: false,
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const updatedGoal = {
        ...goal,
        currentAmount: goal.currentAmount + values.amount,
        updatedAt: new Date().toISOString()
    };
    
    try {
        await updateSavingsGoal(updatedGoal);
        
        // Record streak activity
        if (user?.id) {
          StreakService.recordActivity(user.id, ACTIVITY_TYPES.GOAL_UPDATED);
        }

        toast({
          title: 'Progress Updated',
          description: `You've added ${formatCurrency(values.amount)} to your "${goal.name}" goal.`,
        });
        
        // Reset only the amount, preserve keepOpen state
        form.setValue('amount', 0);
        
        if (!values.keepOpen) {
          setOpen(false);
        }
    } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to update goal progress. Please try again.',
          variant: 'destructive'
        });
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
            <Edit className="h-4 w-4 mr-2" />
            Update Progress
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update "{goal.name}"</DialogTitle>
          <DialogDescription>
            Add a transaction amount to this goal. Your current progress is {formatCurrency(goal.currentAmount)}.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-4"
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount to Add</FormLabel>
                  <FormControl>
                    <Input type="number" step="0.01" placeholder={`${getCurrentCurrencySymbol()} 5,000`} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="keepOpen"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Keep open to add more
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Check this to add multiple contributions in one go.
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
                <DialogClose asChild>
                    <Button type="button" variant="ghost">Cancel</Button>
                </DialogClose>
                <Button type="submit">Save Contribution</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
